import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import { Music, Search as SearchIcon } from "lucide-react";

import SiteHeader from "@/components/lemonwire/SiteHeader";
import TopLinksBar from "@/components/lemonwire/TopLinksBar";
import MarqueeBanner from "@/components/lemonwire/MarqueeBanner";
import RetroTabs from "@/components/lemonwire/RetroTabs";
import RetroToolbar from "@/components/lemonwire/RetroToolbar";
import LeftSidebar from "@/components/lemonwire/LeftSidebar";
import RightSidebar from "@/components/lemonwire/RightSidebar";
import BottomStatusBar from "@/components/lemonwire/BottomStatusBar";
import SiteFooter from "@/components/lemonwire/SiteFooter";

import SearchBar from "@/components/lemonwire/SearchBar";
import ResultsTable from "@/components/lemonwire/ResultsTable";
import FreshRips from "@/components/lemonwire/FreshRips";
import DownloadsView from "@/components/lemonwire/DownloadsView";
import QuarantineView from "@/components/lemonwire/QuarantineView";
import WinampPlayer from "@/components/lemonwire/WinampPlayer";
import PlaylistQueue from "@/components/lemonwire/PlaylistQueue";
import Panel from "@/components/lemonwire/Panel";
import VirusScanModal from "@/components/lemonwire/VirusScanModal";
import AboutModal from "@/components/lemonwire/AboutModal";
import FullResultsModal from "@/components/lemonwire/FullResultsModal";
import SparkleCursor from "@/components/lemonwire/SparkleCursor";
import StickyMiniPlayer from "@/components/lemonwire/StickyMiniPlayer";
import BootScreen from "@/components/lemonwire/BootScreen";
import MixtapeModal from "@/components/lemonwire/MixtapeModal";
import ThemeAndExtras from "@/components/lemonwire/ThemeAndExtras";
import TellAFriendModal from "@/components/lemonwire/TellAFriendModal";

import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import useKeyboardShortcuts from "@/hooks/useKeyboardShortcuts";
import { api } from "@/lib/api";
import { pickFakeBitrate } from "@/lib/fakeData";

const HOT_QUERIES = ["Britney Spears", "Linkin Park", "Evanescence", "50 Cent", "Eminem", "Avril Lavigne", "Outkast", "Eiffel 65"];

export default function LemonWire() {
  const [tab, setTab] = useState("search");
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");

  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [fresh, setFresh] = useState([]);
  const [freshLoading, setFreshLoading] = useState(true);

  const [stats, setStats] = useState(null);
  const [scanTarget, setScanTarget] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [showFullResults, setShowFullResults] = useState(false);
  const [sparklesOn, setSparklesOn] = useState(true);
  const [showMixtape, setShowMixtape] = useState(false);
  const [showTellFriend, setShowTellFriend] = useState(false);
  const [miniPlayerHidden, setMiniPlayerHidden] = useState(false);
  const [quarantine, setQuarantine] = useState(() => {
    try {
      const raw = localStorage.getItem("lw_quarantine_v1");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("lw_theme") || "luna"; } catch { return "luna"; }
  });
  const [booting, setBooting] = useState(() => {
    try { return localStorage.getItem("lw_seen_boot") !== "1"; } catch { return true; }
  });

  // Persist quarantine
  useEffect(() => {
    try { localStorage.setItem("lw_quarantine_v1", JSON.stringify(quarantine)); } catch { /* ignored */ }
  }, [quarantine]);

  const audio = useAudioPlayer();

  // Keyboard shortcuts (space/arrows/slash/esc). Skipped when typing in inputs.
  useKeyboardShortcuts({
    onPlayPause: audio.toggle,
    onNext: audio.next,
    onPrev: audio.prev,
    onFocusSearch: () => {
      setTab("search");
      setTimeout(() => {
        const el = document.querySelector('[data-testid="search-input"]');
        el?.focus();
      }, 50);
    },
    onEscape: () => {
      if (showFullResults) setShowFullResults(false);
      else if (showMixtape) setShowMixtape(false);
      else if (showTellFriend) setShowTellFriend(false);
      else if (showAbout) setShowAbout(false);
      else if (scanTarget) setScanTarget(null);
    },
  });

  // Apply theme to <html> class so CSS can switch palettes.
  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove("theme-luna", "theme-royale");
    html.classList.add(theme === "royale" ? "theme-royale" : "theme-luna");
    try { localStorage.setItem("lw_theme", theme); } catch { /* ignored */ }
  }, [theme]);

  // Whenever a new track starts playing, re-open the sticky mini player
  // (but only if it was explicitly closed — avoids fighting the user on first load).
  const prevTrackIdRef = useRef(null);
  useEffect(() => {
    const newId = audio.currentTrack?.id || null;
    if (newId && newId !== prevTrackIdRef.current) {
      setMiniPlayerHidden(false);
    }
    prevTrackIdRef.current = newId;
  }, [audio.currentTrack?.id]);

  // ===== Effects =====
  useEffect(() => {
    let cancelled = false;
    setFreshLoading(true);
    api.fresh(24).then((data) => {
      if (!cancelled) setFresh(data.tracks || []);
    }).catch(() => {
      if (!cancelled) setFresh([]);
    }).finally(() => {
      if (!cancelled) setFreshLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const loadStats = () => api.stats().then(setStats).catch(() => {});
    loadStats();
    const t = setInterval(loadStats, 8000);
    return () => clearInterval(t);
  }, []);

  // ===== Handlers =====
  const doSearch = useCallback(async (q) => {
    setActiveQuery(q);
    setSearchLoading(true);
    setResults([]);
    setTab("search");
    try {
      const data = await api.search(q, 30);
      setResults(data.tracks || []);
      if ((data.tracks || []).length === 0) {
        toast.info("No bangers matched that query. Try a simpler term.");
      }
    } catch {
      toast.error("Peer swarm unreachable. Try again.");
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSelectTrack = useCallback((track) => {
    if (!track.preview_url) {
      toast.error("Preview not available for this file. Peer offline?");
      return;
    }
    setScanTarget(track);
  }, []);

  const handleScanComplete = useCallback((track, result) => {
    setScanTarget(null);
    setMiniPlayerHidden(false); // Re-open sticky mini whenever a new song plays
    audio.enqueueAndPlay(track);
    const { status, threats } = result || {};
    if (threats && threats.length > 0) {
      toast.warning(`Now playing: ${track.artist} — ${track.title}`, {
        description: `${threats.length} "threat${threats.length === 1 ? "" : "s"}" ignored. You brave soul.`,
        duration: 3200,
      });
    } else if (status === "unverified") {
      toast.success(`Now playing: ${track.artist} — ${track.title}`, {
        description: "Heuristic scan: 0 real threats. Enjoy.",
        duration: 2600,
      });
    } else {
      toast.success(`Now playing: ${track.artist} — ${track.title}`, {
        description: "0 threats detected. Safe to headbang.",
        duration: 2600,
      });
    }
  }, [audio]);

  const handleQuarantine = useCallback((track, threats) => {
    setScanTarget(null);
    setQuarantine((q) => {
      // Replace any prior entry for the same track with the newer one
      const filtered = q.filter((it) => it.track.id !== track.id);
      return [
        {
          track,
          threats: threats || [],
          status: threats && threats.length >= 2 ? "suspicious" : "unverified",
          quarantinedAt: Date.now(),
        },
        ...filtered,
      ];
    });
    toast.error(`Quarantined: ${track.title}`, {
      description: `${threats?.length || 0} threat${(threats?.length || 0) === 1 ? "" : "s"} moved to the vault.`,
      duration: 2600,
    });
  }, []);

  const handleRestore = useCallback((item) => {
    setQuarantine((q) => q.filter((it) => it.track.id !== item.track.id));
    audio.addToQueue(item.track);
    toast.success(`Restored: ${item.track.title}`, {
      description: "Released from quarantine and added to your queue. (You brave soul.)",
      duration: 2400,
    });
  }, [audio]);

  const handlePurgeOne = useCallback((item) => {
    setQuarantine((q) => q.filter((it) => it.track.id !== item.track.id));
    toast(`Purged ${item.track.title} from the vault.`, { duration: 1600 });
  }, []);

  const handlePurgeAll = useCallback(() => {
    setQuarantine([]);
    toast.success("Quarantine vault emptied.", { duration: 1600 });
  }, []);

  const handleGenreSelect = useCallback(async (genre) => {
    setTab("search");
    setQuery(genre);
    await doSearch(genre);
  }, [doSearch]);

  const handleToolbar = useCallback((key) => {
    if (key === "home") { setTab("search"); setQuery(""); setActiveQuery(""); setResults([]); }
    else if (key === "refresh") {
      setFreshLoading(true);
      api.fresh(24).then((d) => setFresh(d.tracks || [])).finally(() => setFreshLoading(false));
      toast("Refreshed swarm connection.");
    } else if (key === "scanner") {
      toast.success("LemonShield™ is active. 0 threats found this week.");
    } else if (key === "turbo") {
      toast("TurboPeers™ enabled. (It didn't actually do anything.)");
    } else if (key === "favs") {
      toast("Favorites coming soon. You know, like every 2000s site.");
    } else if (key === "history") {
      toast("No search history stored. We respect your 2003 privacy.");
    } else if (key === "hdd") {
      toast("C:\\ has 3.2GB free of 40GB. You're fine.");
    } else if (key === "options") {
      toast("Options > Advanced > Legacy > Nostalgia: set to MAX");
    }
  }, []);

  // ===== Computed =====
  const queueIds = useMemo(() => audio.queue.map((t) => t.id), [audio.queue]);
  const currentBitrate = audio.currentTrack ? pickFakeBitrate(audio.currentTrack) : 128;

  // ===== Render main content by tab =====
  // NOTE: Rendered as a function call (not a JSX component) so it does NOT
  // remount its children on every parent render (that's what was killing the
  // search input's focus on every keystroke).
  const renderMain = () => {
    if (tab === "search") {
      return (
        <div className="flex flex-col gap-2 flex-1 min-h-0">
          <SearchBar value={query} onChange={setQuery} onSearch={doSearch} loading={searchLoading} />
          <div className="flex items-center gap-1.5 text-[12px] flex-wrap">
            <span className="text-[#333] font-bold">Hot queries:</span>
            {HOT_QUERIES.map((q) => (
              <button
                key={q}
                data-testid={`hot-query-${q}`}
                onClick={() => { setQuery(q); doSearch(q); }}
                className="bevel-btn px-2.5 py-[3px] text-[11px]"
              >
                {q}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-[#555] italic">
              {results.length > 0 ? `${results.length} files · 0.42 sec · 0 viruses` : "Ready."}
            </span>
          </div>

          {activeQuery ? (
            <ResultsTable
              tracks={results}
              loading={searchLoading}
              onSelect={handleSelectTrack}
              queueIds={queueIds}
              onExpand={() => setShowFullResults(true)}
            />
          ) : (
            <FreshRips
              tracks={fresh.slice(0, 8)}
              loading={freshLoading}
              onSelect={handleSelectTrack}
              title="Most Popular Today — Click to Play"
            />
          )}
        </div>
      );
    }
    if (tab === "library") {
      return (
        <FreshRips
          tracks={fresh}
          loading={freshLoading}
          onSelect={handleSelectTrack}
          title="Library · Fresh Rips from the Swarm"
        />
      );
    }
    if (tab === "downloads") {
      return <DownloadsView queue={audio.queue} />;
    }
    if (tab === "quarantine") {
      return (
        <QuarantineView
          items={quarantine}
          onRestore={handleRestore}
          onPurgeOne={handlePurgeOne}
          onPurgeAll={handlePurgeAll}
        />
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen py-4 tiled-bg">
      <SparkleCursor enabled={sparklesOn} />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            fontFamily: "Tahoma, Verdana, sans-serif",
            fontSize: "12px",
            border: "2px solid #003c9e",
            background: "#ECE9D8",
            color: "#000",
            borderRadius: 2,
            boxShadow: "3px 3px 0 rgba(0,0,0,0.3)",
          },
        }}
      />

      <div className="site-shell">
        <TopLinksBar />
        <SiteHeader
          onTellFriend={() => setShowTellFriend(true)}
          onGetPro={() => toast("LemonWire Pro purchase coming soon™. For now: enjoy the free tier with ads.", { duration: 2400 })}
        />

        <div className="mt-1"><MarqueeBanner stats={stats} /></div>

        {/* 3-column content layout */}
        <div className="flex gap-2 mt-2">
          <div className="w-[210px] shrink-0">
            <LeftSidebar
              stats={stats}
              onGenreSelect={handleGenreSelect}
              onAbout={() => setShowAbout(true)}
              onGuestbook={() => toast("The guestbook is full of anonymous compliments. Thanks ❤️")}
              onTellFriend={() => setShowTellFriend(true)}
              theme={theme}
              onThemeChange={(t) => {
                setTheme(t);
                toast(t === "royale" ? "Skin changed to Royale Noir 🌙" : "Skin changed to Luna ☀️", { duration: 1400 });
              }}
              onReplayBoot={() => {
                try { localStorage.removeItem("lw_seen_boot"); } catch { /* ignored */ }
                setBooting(true);
              }}
              onOpenMixtape={() => {
                if (audio.queue.length === 0) {
                  toast.error("Queue is empty — add some bangers first!");
                  return;
                }
                setShowMixtape(true);
              }}
            />
          </div>

          {/* Main column */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="bevel-out bg-[#ECE9D8] flex flex-col overflow-hidden">
              <RetroTabs active={tab} onChange={setTab} quarantineCount={quarantine.length} downloadsCount={audio.queue.length || 3} />
              <RetroToolbar onAction={handleToolbar} />
              <div className="p-2 bg-[#ECE9D8] flex flex-col gap-2" style={{ minHeight: 460 }}>
                {renderMain()}
              </div>
              <BottomStatusBar
                stats={stats}
                bitrate={currentBitrate}
                isPlaying={audio.isPlaying}
              />
            </div>

            {/* Now Playing dock */}
            <div className="mt-2 grid grid-cols-[minmax(0,1fr)_260px] gap-2">
              <Panel
                data-testid="now-playing-panel"
                title="Now Playing"
                icon={Music}
                variant="green"
                contentClassName="bg-[#0E0E14] p-0"
              >
                <WinampPlayer
                  audio={audio}
                  miniHidden={miniPlayerHidden}
                  onShowMini={() => {
                    setMiniPlayerHidden(false);
                    toast("Mini player restored ✨", { duration: 1200 });
                  }}
                />
              </Panel>

              <Panel
                data-testid="queue-panel"
                title={`Playlist Queue (${audio.queue.length})`}
                icon={SearchIcon}
                variant="blue"
                contentClassName="p-0 bg-white"
              >
                <div style={{ height: 280, overflow: "hidden" }} className="flex">
                  <PlaylistQueue audio={audio} embedded />
                </div>
              </Panel>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="w-[210px] shrink-0">
            <RightSidebar />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-2">
          <SiteFooter visitor={stats?.peers_online ? 428000 + (stats.peers_online % 1000) : 428391} />
        </div>
      </div>

      {scanTarget && (
        <VirusScanModal
          track={scanTarget}
          onComplete={handleScanComplete}
          onCancel={() => setScanTarget(null)}
          onQuarantine={handleQuarantine}
        />
      )}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {showFullResults && (
        <FullResultsModal
          tracks={results}
          queueIds={queueIds}
          activeQuery={activeQuery}
          onClose={() => setShowFullResults(false)}
          onSelect={(t) => {
            setShowFullResults(false);
            handleSelectTrack(t);
          }}
        />
      )}

      <StickyMiniPlayer
        audio={audio}
        sparklesOn={sparklesOn}
        hidden={miniPlayerHidden}
        onHide={() => {
          audio.stop();
          setMiniPlayerHidden(true);
          toast("Mini player closed & song stopped.", {
            description: "Click 'Show Mini' on the main player to bring it back.",
            duration: 2400,
          });
        }}
        onSparklesToggle={() => {
          setSparklesOn((s) => {
            toast(s ? "Sparkles disabled" : "Sparkles enabled ✨", { duration: 1200 });
            return !s;
          });
        }}
      />

      {showMixtape && (
        <MixtapeModal
          queue={audio.queue}
          onClose={() => setShowMixtape(false)}
          handle="xXxRaVeKiNg2003xXx"
        />
      )}

      {showTellFriend && (
        <TellAFriendModal
          onClose={() => setShowTellFriend(false)}
          shareUrl={typeof window !== "undefined" ? window.location.origin : "https://lemonwire.fm/"}
        />
      )}

      {booting && <BootScreen onDone={() => setBooting(false)} />}
    </div>
  );
}
